<template>
    <div>
        <mobile-template>
            <div class="flex flex-col h-screen bg-slate-100 overflow-hidden">

                <!-- ═══ HEADER ═══════════════════════════════════════════════════ -->
                <div class="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0">
                    <div class="flex items-center gap-3">
                        <span class="w-2.5 h-2.5 rounded-full bg-green-500 shadow-[0_0_6px_2px_rgba(34,197,94,.4)] animate-pulse" />
                        <div>
                            <p class="text-sm font-bold text-slate-900">En intervention</p>
                            <p class="text-xs text-slate-400">{{ intervention.reference }}</p>
                        </div>
                    </div>
                    <div class="text-right max-w-[42%]">
                        <p class="text-sm font-semibold text-slate-700 truncate capitalize">{{ intervention.client?.prenom + ' ' + intervention.client?.nom }}</p>
                        <p class="text-xs text-slate-400 truncate">{{ intervention.appareil?.app_type?.label || '-' }}</p>
                    </div>
                </div>

                <!-- ═══ PROGRESS BAR ════════════════════════════════════════════ -->
                <div v-if="selectedTab === 'intervention'" class="relative h-7 bg-slate-50 border-b border-gray-200 overflow-hidden shrink-0">
                    <div
                        class="absolute inset-y-0 left-0 bg-orange-500/20 transition-all duration-500"
                        :style="{ width: `${progress * 100}%` }"
                    />
                    <p class="relative z-10 text-center text-xs font-semibold text-slate-500 leading-7">
                        {{ stepLabels[currentStep] }} · {{ Math.round(progress * 100) }}%
                    </p>
                </div>

                <!-- ═══ TABS ═════════════════════════════════════════════════════ -->
                <div class="flex bg-white border-b border-gray-200 shrink-0">
                    <button
                        v-for="tab in ['intervention', 'dossier']"
                        :key="tab"
                        class="flex-1 py-2.5 text-sm font-semibold transition-colors"
                        :class="selectedTab === tab
                            ? 'text-orange-600 border-b-2 border-orange-600'
                            : 'text-slate-400'"
                        @click="selectedTab = tab"
                    >
                        {{ tab === 'intervention' ? 'Intervention' : 'Dossier' }}
                    </button>
                </div>

                <!-- ═══ CONTENT ══════════════════════════════════════════════════ -->
                <div class="flex-1 overflow-y-auto">

                    <!-- ── ONGLET INTERVENTION ── -->
                    <div v-if="selectedTab === 'intervention'" class="p-4 space-y-4">
                        <component
                            :is="currentStepComponent"
                            v-bind="stepProps"
                            @next="goNext"
                            @finalize="handleFinalizeFromStep"
                        />

                        <!-- ═══ FOOTER (dans le scroll) ══════════════════════════ -->
                        <div class="bg-white border border-gray-200 rounded-2xl px-4 flex gap-3" style="padding-top: 1rem; padding-bottom: 1rem;">
                            <!-- Bouton retour -->
                            <button
                                class="flex items-center gap-1.5 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-slate-600 text-sm font-semibold rounded-xl transition-colors"
                                @click="goBack"
                            >
                                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6" stroke-linecap="round"/></svg>
                                Retour
                            </button>

                            <!-- Offline -->
                            <div v-if="!isConnected && isSigningStep" class="flex-1 flex items-center justify-center gap-2 py-3 bg-red-50 rounded-xl text-red-600 text-sm font-semibold">
                                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="1" y1="1" x2="23" y2="23"/><path d="M16.72 11.06A10.94 10.94 0 0119 12.55M5 12.55a10.94 10.94 0 015.17-2.39M10.71 5.05A16 16 0 0122.56 9M1.42 9a15.91 15.91 0 014.7-2.88M8.53 16.11a6 6 0 016.95 0M12 20h.01"/></svg>
                                Connexion requise
                            </div>

                            <!-- Bouton suivant / terminer -->
                            <button
                                v-else-if="!hideFooterNext"
                                class="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-50"
                                :class="isSigningStep || needReturn === true ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-800 hover:bg-slate-900'"
                                @click="handleFooterNextOrExplain"
                            >
                                <template v-if="submitting">
                                    <i class="fas fa-spinner fa-spin"></i>
                                </template>
                                <template v-else>
                                    {{ footerLabel }}
                                    <svg v-if="!isSigningStep && needReturn !== true" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6" stroke-linecap="round"/></svg>
                                </template>
                            </button>
                        </div>

                    </div>

                    <!-- ── ONGLET DOSSIER ── -->
                    <div v-if="selectedTab === 'dossier'" class="p-4 space-y-4">

                        <!-- Client -->
                        <div class="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                            <button
                                class="w-full flex items-center justify-between px-4 py-3.5 text-sm font-semibold text-slate-700"
                                @click="dossierSection = dossierSection === 'client' ? null : 'client'"
                            >
                                <div class="flex items-center gap-2">
                                    <svg class="w-4 h-4 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                    Fiche client
                                </div>
                                <svg class="w-4 h-4 text-slate-400 transition-transform" :class="dossierSection === 'client' ? 'rotate-180' : ''" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6" stroke-linecap="round"/></svg>
                            </button>
                            <div v-if="dossierSection === 'client'" class="px-4 pb-4 border-t border-gray-100 pt-4 space-y-3">
                                <div v-for="field in clientFields" :key="field.key">
                                    <label class="text-xs font-medium text-slate-500 mb-1 block">{{ field.label }}</label>
                                    <input
                                        v-model="editedClient[field.key]"
                                        :type="field.type || 'text'"
                                        class="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-500"
                                    />
                                </div>
                                <button
                                    class="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center disabled:opacity-60"
                                    :disabled="savingClient"
                                    @click="saveClient"
                                >
                                    <span v-if="savingClient" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                    <span v-else>Enregistrer le client</span>
                                </button>
                            </div>
                        </div>

                        <!-- Appareil -->
                        <div class="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                            <button
                                class="w-full flex items-center justify-between px-4 py-3.5 text-sm font-semibold text-slate-700"
                                @click="dossierSection = dossierSection === 'appareil' ? null : 'appareil'"
                            >
                                <div class="flex items-center gap-2">
                                    <svg class="w-4 h-4 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
                                    Appareil
                                </div>
                                <svg class="w-4 h-4 text-slate-400 transition-transform" :class="dossierSection === 'appareil' ? 'rotate-180' : ''" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6" stroke-linecap="round"/></svg>
                            </button>
                            <div v-if="dossierSection === 'appareil'" class="px-4 pb-4 border-t border-gray-100 pt-4 space-y-3">
                                <div v-for="field in appareilFields" :key="field.key">
                                    <label class="text-xs font-medium text-slate-500 mb-1 block">{{ field.label }}</label>
                                    <template v-if="field.key == 'type'">
                                        <select class="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-500" v-model="editedAppareil.type">
                                            <option v-for="type in societeTypesAppareil" :key="type.key" :value="type.key">
                                                {{ type.label }}
                                            </option>
                                        </select>
                                    </template>
                                    <template v-else>
                                        <input
                                            v-model="editedAppareil[field.key]"
                                            :type="field.type || 'text'"
                                            class="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-orange-500"
                                        />
                                    </template>
                                </div>
                                <button
                                    class="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center disabled:opacity-60"
                                    :disabled="savingAppareil"
                                    @click="saveAppareil"
                                >
                                    <span v-if="savingAppareil" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                    <span v-else>Enregistrer l'appareil</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ═══ OVERLAY FINALISATION ════════════════════════════════════ -->
                <transition name="fade">
                    <div v-if="endingMission" class="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-6">
                        <div class="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
                            <p class="text-base font-bold text-slate-900 mb-5">Finalisation en cours…</p>
                            <div class="space-y-3">
                                <div
                                    v-for="step in finalizeSteps"
                                    :key="step.key"
                                    class="flex items-center gap-3"
                                >
                                    <div class="w-6 h-6 flex items-center justify-center shrink-0">
                                        <template v-if="currentFinalizeStep === step.key && !doneSteps.includes(step.key)">
                                            <i class="fas fa-spinner fa-spin text-orange-600"></i>
                                        </template>
                                        <span v-else-if="doneSteps.includes(step.key)"
                                            class="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                            <svg class="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5" stroke-linecap="round"/></svg>
                                        </span>
                                        <span v-else class="w-5 h-5 rounded-full border-2 border-slate-200" />
                                    </div>
                                    <span
                                        class="text-sm"
                                        :class="doneSteps.includes(step.key)
                                            ? 'text-green-600 font-semibold'
                                            : currentFinalizeStep === step.key
                                                ? 'text-orange-600 font-semibold'
                                                : 'text-slate-400'"
                                    >
                                        {{ step.label }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>

            </div>
        </mobile-template>
    </div>
</template>

<script>
import axios from 'axios'
import StepDocumentation   from './steps/StepDocumentation.vue';
import StepTravaux         from './steps/StepTravaux.vue';
import StepSerialNumber    from './steps/StepSerialNumber.vue';
import StepDocuments       from './steps/StepDocuments.vue';
import StepPhotoFin        from './steps/StepPhotoFin.vue';
import StepChantierEnd     from './steps/StepChantierEnd.vue';
import StepSignatureType   from './steps/StepSignatureType.vue';
import StepSignature       from './steps/StepSignature.vue';
import StepSignatureEmail  from './steps/StepSignatureEmail.vue';

const STEP_COMPONENT_MAP = {
    'documentation'   : StepDocumentation,
    'travaux'         : StepTravaux,
    'serial-number'   : StepSerialNumber,
    'documents'       : StepDocuments,
    'photo-fin'       : StepPhotoFin,
    'chantier-end'    : StepChantierEnd,
    'signature-type'  : StepSignatureType,
    'signature'       : StepSignature,
    'signature-email' : StepSignatureEmail,
};

const STEP_LABELS = {
    'documentation'   : 'Docs',
    'travaux'         : 'Travaux',
    'serial-number'   : 'N° série',
    'documents'       : 'Documents',
    'photo-fin'       : 'Photo fin',
    'chantier-end'    : 'Clôture',
    'signature-type'  : 'Signature',
    'signature'       : 'Signature',
    'signature-email' : 'Signature',
};

export default {
    name: 'WorkMode',

    provide() {
        // On expose le composant lui-même pour que les enfants puissent lire/écrire
        return { wm: this };
    },

    props: {
        intervention: { type: Object, required: true },
        // Callback de fin de mission
        // onEndMission: { type: Function, required: true },
    },

    data() {
        const isClim         = this.intervention.appareil?.app_type?.has_fluid ? true : false;
        const isEntretien    = this.intervention.intervention_type?.type_logique === 'entretien';
        const isInstallation = this.intervention.intervention_type?.type_logique === 'installation' || this.intervention.intervention_type?.type_logique === 'mise_en_service';

        return {
            // ── Navigation ──────────────────────────────────
            selectedTab: 'intervention',
            currentStep: 'documentation',
            currentDocStep: null,   // 'cerfa' | 'contrat' | 'pv_reception' | 'attestation'

            // ── Formulaire principal ─────────────────────────
            formData: {
                imageDebut: null, imageDebutLocal: null,
                imageFin: null,   imageFinLocal: null,
                typeInterventionDetails: [],
                cerfa: { operations: [], fluide_recupere: null, fluide_ajoute: null,
                    tirage_vide: 'non', tirage_vide_duree: null,
                    etancheite: 'non', etancheite_methode: [], photo_plaque: null },
                signature_client: null, signatureClientLocal: null,
                photos: [],
                chantier_repassage: false, chantier_repassage_motif: '',
                fin_chantier: false,
                appareil: null,
            },
            formLoaded: false,

            // ── Constants ────────────────────────────────────
            stepLabels: STEP_LABELS,
            STORAGE_KEY: `intervention-form-${this.intervention.id}`,

            // Référence vers CerfaForm (auto-enregistrée par le composant enfant via wm)
            _cerfaFormInstance: null,

            // ── Docs à générer ──────────────────────────────
            docsToGenerate: {
                bon_intervention: true,
                cerfa: isClim,
                contrat_entretien: isEntretien,
                attestation_entretien: isEntretien || isInstallation,
                pv_reception: isInstallation,
            },

            // ── Chantier end ─────────────────────────────────
            canEndChantier: null,
            checkingCanEnd: false,
            needReturn: null,
            returnReason: '',

            // ── Dossier ──────────────────────────────────────
            editedClient:  { ...this.intervention.client  },
            editedAppareil:{ ...this.intervention.appareil },
            savingClient:  false,
            savingAppareil:false,
            dossierSection: null,

            // ── Réseau ────────────────────────────────────────
            isConnected: true,

            // ── Finalisation ─────────────────────────────────
            endingMission: false,
            submitting:    false,
            finalizeSteps: [],
            currentFinalizeStep: null,
            doneSteps: [],

            // ── Constants ────────────────────────────────────
            stepLabels: STEP_LABELS,
            STORAGE_KEY: `intervention-form-${this.intervention.id}`,
        };
    },

    computed: {
        isClim()         { return this.intervention.appareil?.app_type?.has_fluid ? true : false },
        isEntretien()    { return this.intervention.intervention_type?.type_logique === 'entretien'; },
        isInstallation() { return this.intervention.intervention_type?.type_logique === 'installation' || this.intervention.intervention_type?.type_logique === 'mise_en_service'; },
        needsSerialNumber() { return !this.intervention.appareil?.numero_serie?.trim() || !this.intervention.appareil?.marque?.trim() || !this.intervention.appareil?.model?.trim(); },

        activeDocSteps() {
            return [
                this.docsToGenerate.cerfa               ? 'cerfa'        : null,
                this.docsToGenerate.contrat_entretien   ? 'contrat'      : null,
                this.docsToGenerate.pv_reception        ? 'pv_reception' : null,
                this.docsToGenerate.attestation_entretien ? 'attestation': null,
            ].filter(Boolean);
        },

        mainSteps() {
            return [
                'documentation', 'travaux',
                ...(this.needsSerialNumber ? ['serial-number'] : []),
                ...(this.activeDocSteps.length > 0 ? ['documents'] : []),
                'photo-fin', 'chantier-end', 'signature-type',
            ];
        },

        currentStepIndex() { return this.mainSteps.indexOf(this.currentStep); },
        progress() {
            let progress = this.mainSteps.length > 1 ? this.currentStepIndex / (this.mainSteps.length - 1) : 0;

            // Pas dépasser 100% même si on est sur une étape de documents
            if (progress > 1 || progress < 0) {
                progress = 1;
            }
            return progress;
        },

        currentStepComponent() { return STEP_COMPONENT_MAP[this.currentStep] || null; },

        stepProps() {
            return {
                intervention:    this.intervention,
                formData:        this.formData,
                // travauxList:     this.travauxList,
                docsToGenerate:  this.docsToGenerate,
                activeDocSteps:  this.activeDocSteps,
                currentDocStep:  this.currentDocStep,
                canEndChantier:  this.canEndChantier,
                checkingCanEnd:  this.checkingCanEnd,
                needReturn:      this.needReturn,
                returnReason:    this.returnReason,
            };
        },

        isSigningStep() { return this.currentStep === 'signature' || this.currentStep === 'signature-email'; },

        isNextDisabled() {
            if (this.submitting) return true;

            if (this.currentStep === 'travaux') {
                const list = this.intervention.travaux || [];
                return list.length > 0 && this.formData.typeInterventionDetails.length === 0;
            }

            if (this.currentStep === 'serial-number')
                return !this.editedAppareil.numero_serie
                    || !this.editedAppareil.marque
                    || !this.editedAppareil.model;

            if (this.currentStep === 'photo-fin')
                return !this.formData.imageFinLocal && !this.formData.imageFin;

            if (this.currentStep === 'chantier-end') {
                if (this.needReturn === null) return true;
                if (this.needReturn === true && !this.returnReason.trim()) return true;
                return false;
            }

            if (this.currentStep === 'signature')
                return !this.formData.signatureClientLocal && !this.formData.signature_client;

            if (this.currentStep === 'documents' && this.currentDocStep === 'cerfa') {
                const cerfa = this._cerfaFormInstance;
                if (!cerfa) return true;       // composant pas encore monté
                return !cerfa.isCerfaValid;    // délègue la validation à CerfaForm
            }

            return false;
        },

        nextBlockReason() {
            if (this.submitting) return null; // déjà en cours, pas de message

            if (this.currentStep === 'travaux') {
                const list = this.intervention.travaux || [];
                if (list.length > 0 && this.formData.typeInterventionDetails.length === 0)
                    return 'Sélectionnez au moins un travail réalisé.';
            }

            if (this.currentStep === 'serial-number') {
                if (!this.editedAppareil.numero_serie) return 'Le numéro de série est obligatoire.';
                if (!this.editedAppareil.marque)       return 'La marque est obligatoire.';
                if (!this.editedAppareil.model)        return 'Le modèle est obligatoire.';
            }

            if (this.currentStep === 'photo-fin' && !this.formData.imageFinLocal && !this.formData.imageFin)
                return "Prenez une photo de fin d'intervention avant de continuer.";

            if (this.currentStep === 'chantier-end') {
                if (this.needReturn === null)
                    return 'Indiquez si le chantier est terminé ou si un retour est nécessaire.';
                if (this.needReturn === true && !this.returnReason.trim())
                    return 'Indiquez le motif du retour nécessaire.';
            }

            if (this.currentStep === 'signature' && !this.formData.signatureClientLocal && !this.formData.signature_client)
                return 'La signature du client est requise.';

            if (this.currentStep === 'documents' && this.currentDocStep === 'cerfa') {
                const cerfa = this._cerfaFormInstance;
                if (!cerfa) return 'Le formulaire CERFA est en cours de chargement.';
                if (!cerfa.isCerfaValid) {
                    // Retourner la première erreur CERFA pour guider précisément
                    return cerfa.validation.errors[0] || 'Le CERFA est incomplet.';
                }
            }

            return null; // rien de bloquant
        },

        hideFooterNext() {
            return this.currentStep === 'chantier-end'
                && !this.checkingCanEnd
                && this.canEndChantier === false
                && this.needReturn === null;
        },

        footerLabel() {
            if (this.currentStep === 'chantier-end' && this.needReturn === true) return "Terminer l'intervention";
            if (this.isSigningStep) return "Terminer l'intervention";
            return 'Continuer';
        },

        clientFields() {
            return [
                { key: 'nom',    label: 'Nom' },
                { key: 'prenom', label: 'Prénom' },
                { key: 'email',  label: 'Email', type: 'email' },
                { key: 'telephone', label: 'Téléphone', type: 'tel' },
            ];
        },

        appareilFields() {
            return [
                { key: 'type',             label: 'Type' },
                { key: 'marque',           label: 'Marque' },
                { key: 'model',            label: 'Modèle' },
                { key: 'numero_serie',     label: 'N° de série' },
                { key: 'installation_date',label: 'Date installation', type: 'date' },
                { key: 'emplacement',      label: 'Emplacement' },
                { key: 'note',             label: 'Note' },
            ];
        },

        societeTypesAppareil() {
            let types = localStorage.getItem('mobileCategorieAppareil');
            if (types) {
                try {
                    types = JSON.parse(types);
                    return types || []
                } catch {
                    return [];
                }
            }
        }
    },

    watch: {
        formData: {
            deep: true,
            handler(val) {
                if (!this.formLoaded) return;
                try { localStorage.setItem(this.STORAGE_KEY, JSON.stringify(val)); } catch {}
            },
        },
        currentStep(val) {
            if (val === 'chantier-end') this.fetchCanEndChantier();
        },
    },

    created() {
        this.loadLocalForm();
        this.watchNetwork();
    },

    beforeDestroy() {
        if (this._netUnsubscribe) this._netUnsubscribe();
    },

    methods: {

        /* ================================================================
         * PERSISTANCE LOCALE
         * ================================================================ */

        loadLocalForm() {
            try {
                const saved = localStorage.getItem(this.STORAGE_KEY);
                if (saved) {
                    this.formData = { ...this.formData, ...JSON.parse(saved) };
                    const a = this.formData.appareil;
                    if (a?.numero_serie) this.editedAppareil.numero_serie = a.numero_serie;
                    if (a?.marque)       this.editedAppareil.marque       = a.marque;
                    if (a?.model)        this.editedAppareil.model        = a.model;
                }
            } catch {}
            this.formLoaded = true;
        },

        clearLocalStorage() {
            const keys = [
                this.STORAGE_KEY,
                `cerfa_draft_${this.intervention.id}`,
                `intervention:${this.intervention.id}:contrat_entretien`,
                `intervention:${this.intervention.id}:attestation_entretien`,
                `intervention:${this.intervention.id}:pv_reception`,
            ];
            keys.forEach((k) => { try { localStorage.removeItem(k); } catch {} });
        },

        /* ================================================================
         * RÉSEAU
         * ================================================================ */

        watchNetwork() {
            const update = () => { this.isConnected = navigator.onLine; };
            window.addEventListener('online', update);
            window.addEventListener('offline', update);
            this._netUnsubscribe = () => {
                window.removeEventListener('online', update);
                window.removeEventListener('offline', update);
            };
            update();
        },

        /* ================================================================
         * MÉTHODE EXPOSÉE : mise à jour du formData depuis les enfants
         * ================================================================ */

        updateFormData(patch) {
            this.formData = { ...this.formData, ...patch };
        },

        /* ================================================================
         * NAVIGATION
         * ================================================================ */

        goNext() {
            if (this.currentStep === 'documents' && this.activeDocSteps.length > 0) {
                if (this.currentDocStep === null) { this.currentDocStep = this.activeDocSteps[0]; return; }
                const idx = this.activeDocSteps.indexOf(this.currentDocStep);
                if (idx < this.activeDocSteps.length - 1) { this.currentDocStep = this.activeDocSteps[idx + 1]; return; }
            }
            const idx = this.mainSteps.indexOf(this.currentStep);
            if (idx < this.mainSteps.length - 1) {
                const next = this.mainSteps[idx + 1];
                this.currentStep = next;
                if (next === 'documents') this.currentDocStep = this.activeDocSteps[0] ?? null;
            }
        },

        goBack() {
            if (this.currentStep === 'signature' || this.currentStep === 'signature-email') {
                this.currentStep = 'signature-type'; return;
            }
            if (this.currentStep === 'documents' && this.currentDocStep !== null) {
                const idx = this.activeDocSteps.indexOf(this.currentDocStep);
                if (idx > 0) { this.currentDocStep = this.activeDocSteps[idx - 1]; return; }
                const mIdx = this.mainSteps.indexOf('documents');
                if (mIdx > 0) { this.currentStep = this.mainSteps[mIdx - 1]; this.currentDocStep = null; }
                return;
            }
            const idx = this.mainSteps.indexOf(this.currentStep);
            if (idx > 0) this.currentStep = this.mainSteps[idx - 1];
        },

        handleFooterNextOrExplain() {
            const reason = this.nextBlockReason;
            if (reason) {
                this.$toast.error(reason);
                // Déclencher showErrors dans CerfaForm si on est sur le CERFA
                if (this.currentStep === 'documents' && this.currentDocStep === 'cerfa') {
                    const cerfa = this._cerfaFormInstance;
                    if (cerfa) cerfa.showErrors = true;
                }
                return;
            }
            this.handleFooterNext();
        },

        handleFinalizeFromStep({ signatureMode, finChantier }) {
            this.finalizeMission(signatureMode, finChantier);
        },

        /* ================================================================
         * FOOTER NEXT
         * ================================================================ */

        async handleFooterNext() {
            if (this.currentStep === 'chantier-end') {
                if (this.needReturn === true) { await this.finalizeMission(null, false); return; }
                this.currentStep = 'signature-type'; return;
            }
            if (this.currentStep === 'signature')       { await this.finalizeMission('on-site', true); return; }
            if (this.currentStep === 'signature-email') { await this.finalizeMission('email',   true); return; }
            this.goNext();
        },

        /* ================================================================
         * VÉRIFICATION CHANTIER
         * ================================================================ */

        async fetchCanEndChantier() {
            this.checkingCanEnd = true;
            try {
                let response = await this.$request.post(`/chantiers/${this.intervention.chantier_id}/can-end`, {
                    chantier_id: this.intervention.chantier_id,
                });

                this.canEndChantier = typeof response === 'object' && response !== null 
                    ? response.api 
                    : response;
            } catch {
                this.canEndChantier = false;
            } finally {
                this.checkingCanEnd = false;
            }
        },

        /* ================================================================
         * UPLOAD DES ASSETS LOCAUX
         * ================================================================ */
        base64ToBlob(base64) {
            const arr = base64.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);

            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }

            return new Blob([u8arr], { type: mime });
        },

        async uploadBase64ToStorage(base64, key, filename = 'file.jpg') {
            const blob = this.base64ToBlob(base64);

            const formData = new FormData();
            formData.append('key_name', key);
            formData.append('file', blob, filename);

            const response = await axios.post(
                'https://storage.termoflow.fr/upload.php',
                formData,
                {
                    headers: {
                        Authorization: 'Bearer PRODt1ntH4nCMTfw5tGrXy0ZRgyz4Q',
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (!response.data?.success) {
                throw new Error('Upload échoué');
            }

            return response.data.url;
        },

        async uploadFinalAssets() {
            const d = { ...this.formData };

            const uploads = [
                { local: 'imageDebutLocal',      remote: 'imageDebut',        key: 'image_debut_intervention' },
                { local: 'imageFinLocal',        remote: 'imageFin',          key: 'image_fin_intervention' },
                { local: 'signatureClientLocal', remote: 'signature_client',  key: 'client_signature' },
            ];

            await Promise.all(
                uploads.map(async (u) => {
                    // URL distante déjà présente → juste nettoyer le local
                    if (d[u.remote]) {
                        delete d[u.local];
                        return;
                    }
                    if (d[u.local]) {
                        try {
                            const url = await this.uploadBase64ToStorage(d[u.local], u.key, `${u.key}_${this.intervention.id}.png`);
                            d[u.remote] = url;
                        } catch (e) {
                            console.error(`Erreur upload ${u.key}`, e);
                            // On ne met PAS le base64 en fallback — on laisse vide
                        } finally {
                            delete d[u.local]; // toujours supprimer le base64 du payload
                        }
                    }
                })
            );

            this.formData = d;
            return d;
        },

        /* ================================================================
         * ENVOI DES DOCUMENTS
         * ================================================================ */

        async sendCerfa(fd) {
            const raw = localStorage.getItem(`cerfa_draft_${this.intervention.id}`);
            if (!raw) return;
            const { form } = JSON.parse(raw);
            if (!form?.fluide_denomination) return;
            form.intervention_id = this.intervention.id;
            if (fd.signature_client) form.signature_client = fd.signature_client;
            await this.$request.post('/cerfa/create', form);
            localStorage.removeItem(`cerfa_draft_${this.intervention.id}`);
        },

        async sendContrat(fd) {
            const raw = localStorage.getItem(`intervention:${this.intervention.id}:contrat_entretien`);
            if (!raw) return;
            const c = JSON.parse(raw);
            if (!c.appareil_id || !c.date_debut || !c.date_fin) return;
            if (fd.signature_client) c.url_signature_client = fd.signature_client;
            await this.$request.post('/contrats/contrat-entretien/create', c);
            localStorage.removeItem(`intervention:${this.intervention.id}:contrat_entretien`);
        },

        async sendAttestation(fd) {
            const raw = localStorage.getItem(`intervention:${this.intervention.id}:attestation_entretien`);
            if (!raw) return;
        
            const a = JSON.parse(raw);
        
            // Champs obligatoires minimum
            if (!a.appareil_id || !a.adresse_id || !a.date_intervention) return;
        
            // ── Reconstruction du tableau points_controle ──────────────────────
            // Le composant stocke points_controle comme un objet plat { key: value }.
            // Le backend attend un tableau [{ key, label, section, value }].
            // On relit mobilePointsControle pour retrouver label + section de chaque key.
        
            let pointsControleArray = [];
        
            const pointsRaw = localStorage.getItem('mobilePointsControle');
            if (pointsRaw && a.points_controle && typeof a.points_controle === 'object') {
                try {
                    const allPoints = JSON.parse(pointsRaw);
                    const appType    = this.intervention?.appareil?.type;
                    const typeLogique = this.intervention?.intervention_type?.type_logique;
        
                    // Même logique de résolution que dans le composant
                    let pointsMap = null;
        
                    if (appType && allPoints[appType]) {
                        const byType = allPoints[appType];
                        if (typeLogique && byType[typeLogique]) {
                            pointsMap = byType[typeLogique];
                        } else {
                            const firstKey = Object.keys(byType)[0];
                            if (firstKey) pointsMap = byType[firstKey];
                        }
                    }
        
                    // Fallback : structure directe { [section]: [points] }
                    if (!pointsMap && allPoints && typeof allPoints === 'object') {
                        const firstVal = Object.values(allPoints)[0];
                        if (Array.isArray(firstVal)) pointsMap = allPoints;
                    }
        
                    if (pointsMap) {
                        pointsControleArray = Object.entries(pointsMap).flatMap(([sectionName, points]) =>
                            points.map(p => ({
                                key:     p.key,
                                label:   p.label,
                                section: p.section || sectionName,
                                value:   a.points_controle[p.key] ?? null,
                            }))
                        );
                    }
                } catch (e) {
                    console.error('[sendAttestation] Erreur reconstruction points_controle', e);
                    // Fallback dégradé : on envoie ce qu'on a sous forme de tableau brut
                    pointsControleArray = Object.entries(a.points_controle).map(([key, value]) => ({
                        key,
                        label: key,
                        section: 'Général',
                        value,
                    }));
                }
            }
        
            // ── Payload final ──────────────────────────────────────────────────
            const payload = {
                appareil_id:              a.appareil_id,
                adresse_id:               a.adresse_id,
                date_intervention:        a.date_intervention,
                commentaire_intervenant:  a.commentaire_intervenant ?? null,
                points_controle:          pointsControleArray,
                // Injectés par le parent
                client_id:                this.intervention.client.id,
                intervention_id:          this.intervention.id,
                url_signature_client:     fd.signature_client ?? null,
            };
        
            await this.$request.post('/attestations/store', payload);
            localStorage.removeItem(`intervention:${this.intervention.id}:attestation_entretien`);
        },

        async sendPvReception(fd) {
            const raw = localStorage.getItem(`intervention:${this.intervention.id}:pv_reception`);
            if (!raw) return;
            const pv = JSON.parse(raw);
            if (!pv.intervention_id) return;
            if (fd.signature_client) pv.signature_client = fd.signature_client;
            await this.$request.post('/pv_reception/store', pv);
            localStorage.removeItem(`intervention:${this.intervention.id}:pv_reception`);
        },

        /* ================================================================
         * FINALISATION
         * ================================================================ */

        async finalizeMission(signatureMode, finChantier) {
            this.endingMission = true;
            this.submitting    = true;
            this.doneSteps     = [];
            this.currentFinalizeStep = null;

            try {
                // Remplace le bloc actuel (les ~5 lignes avec if imageDebutLocal...)
                const LOCAL_KEYS = ['imageDebutLocal', 'imageFinLocal', 'signatureClientLocal'];

                let fd = { ...this.formData, fin_chantier: finChantier };

                // Upload si nécessaire + nettoyage des clés base64 dans tous les cas
                if (LOCAL_KEYS.some(k => fd[k])) {
                    fd = await this.uploadFinalAssets();
                    fd.fin_chantier = finChantier;
                }

                // Garantie : on ne laisse jamais partir de base64 dans le payload
                LOCAL_KEYS.forEach(k => delete fd[k]);

                const steps = [];

                let hasAppareilChanges = false
                if (fd.appareil?.numero_serie) {
                    hasAppareilChanges = true
                }
                if (fd.appareil?.marque) {
                    hasAppareilChanges = true
                }
                if (fd.appareil?.model) {
                    hasAppareilChanges = true
                }
                if (hasAppareilChanges) {
                    steps.push({
                        key: 'update_appareil',
                        label: "Mise à jour de l'appareil",
                        run: async () => {
                            await this.$request.post(`/client-appareils/${this.intervention.appareil.id}`, {
                                ...this.intervention.appareil,
                                numero_serie: fd.appareil.numero_serie ? fd.appareil.numero_serie : this.intervention.appareil.numero_serie,
                                marque:       fd.appareil.marque ? fd.appareil.marque : this.intervention.appareil.marque,
                                model:       fd.appareil.model ? fd.appareil.model : this.intervention.appareil.model,
                                client_id: this.intervention.client.id,
                            });
                        },
                    });
                }
                
                if (this.docsToGenerate.cerfa)
                    steps.push({ key: 'cerfa',        label: 'Création du CERFA',          run: () => this.sendCerfa(fd) });
                if (this.docsToGenerate.contrat_entretien)
                    steps.push({ key: 'contrat',      label: "Contrat d'entretien",         run: () => this.sendContrat(fd) });
                if (this.docsToGenerate.attestation_entretien)
                    steps.push({ key: 'attestation',  label: "Attestation d'entretien",     run: () => this.sendAttestation(fd) });
                if (this.docsToGenerate.pv_reception)
                    steps.push({ key: 'pv_reception', label: 'Procès-verbal de réception',  run: () => this.sendPvReception(fd) });

                const endLabel = finChantier && signatureMode === 'on-site'
                    ? 'Envoi des documents signés'
                    : finChantier && signatureMode === 'email'
                        ? 'Envoi de la demande de signature'
                        : "Clôture de l'intervention";

                steps.push({
                    key: 'end_intervention',
                    label: endLabel,
                    run: async () => {
                        await this.$request.post(`/interventions/${this.intervention.id}/terminer`, {
                            fin_chantier: finChantier,
                            intervention: {
                                ...fd,
                                signature_client: finChantier && signatureMode === 'on-site' ? fd.signature_client : undefined,
                            },
                        });
                    },
                });

                steps.push({
                    key: 'cleanup',
                    label: 'Intervention terminée',
                    run: async () => { this.clearLocalStorage(); },
                });

                this.finalizeSteps = steps;

                for (const step of steps) {
                    this.currentFinalizeStep = step.key;
                    await step.run();
                    this.doneSteps = [...this.doneSteps, step.key];
                }

                if (!finChantier && this.intervention.chantier_id) {
                    this.$router.push(`/chantier/${this.intervention.chantier_id}/list`);
                } else {
                    this.$router.push('/');
                    this.$toast.success("Intervention terminée");
                }

                // this.onEndMission();
            } catch (err) {
                console.error('[FINALIZE]', err);
                alert("Impossible de terminer l'intervention. Vérifiez votre connexion.");
            } finally {
                this.endingMission = false;
                this.submitting    = false;
                this.currentFinalizeStep = null;
            }
        },

        /* ================================================================
         * DOSSIER – CLIENT / APPAREIL
         * ================================================================ */

        async saveClient() {
            try {
                this.savingClient = true;
                await this.$request.post(`/clients/${this.editedClient.id}`, this.editedClient);
                this.$notify?.success('Client mis à jour');
            } catch (err) {
                this.$notify?.error(err?.message || 'Impossible de mettre à jour le client');
            } finally {
                this.savingClient = false;
            }
        },

        async saveAppareil() {
            try {
                this.savingAppareil = true;
                await this.$request.post(`/client-appareils/${this.editedAppareil.id}`, {
                    ...this.editedAppareil,
                    client_id: this.intervention.client.id,
                });
                this.$notify?.success('Appareil mis à jour');
            } catch (err) {
                this.$notify?.error(err?.message || "Impossible de mettre à jour l'appareil");
            } finally {
                this.savingAppareil = false;
            }
        },
    },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .2s; }
.fade-enter, .fade-leave-to           { opacity: 0; }
</style>
